/**
 * @file ExportLayoutOptions.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-09
 * @modified 2026-09-10
 * @description Orientation and table fit, for every export in the system.
 *
 *              One control block rather than three: the blend export, the table
 *              export and the formulation export all print through the same
 *              pipeline, and the choices that shape a page are the same choices
 *              whichever document it is. Three copies would answer the same
 *              question three slightly different ways.
 *
 *              `fit` is applied by a script inside the document itself, so the
 *              preview and the PDF cannot disagree about what it means — see
 *              puppeteerPdfService.buildDocumentHtml.
 */

import React from 'react';
import './ExportLayoutOptions.css';

const ORIENTATIONS = [
  { key: 'portrait', icon: 'crop_portrait', label: 'Portrait',
    hint: 'More rows per page' },
  { key: 'landscape', icon: 'crop_landscape', label: 'Landscape',
    hint: 'More columns across' }
];

const FITS = [
  { key: 'none', icon: 'crop_free', label: 'As laid out',
    hint: 'No scaling — long tables run onto more pages' },
  { key: 'width', icon: 'width_normal', label: 'Fit page width',
    hint: 'Shrink until nothing runs off the side' },
  { key: 'length', icon: 'height', label: 'Fit page length',
    hint: 'Shrink until it all lands on one page' },
  { key: 'contents', icon: 'table_chart', label: 'Fit contents',
    hint: 'Every column only as wide as it needs' },
  { key: 'custom', icon: 'tune', label: 'Custom scale',
    hint: 'Set the percentage yourself' }
];

function ExportLayoutOptions({ orientation, onOrientation, fit, onFit, scale, onScale }) {
  const activeFit = FITS.find((f) => f.key === fit);
  return (
    <div className="export-layout">
      <div className="export-layout-head">Page layout</div>

      {/* Orientation used to be two big mode cards in a three-column grid,
          which left a hole where a third would have gone and gave a setting
          the same weight as the choice of what the button does. Both page
          settings are chips now, and they read as one group. */}
      <div className="export-layout-line">
        <span className="export-layout-key">Orientation</span>
        <div className="export-layout-fits">
          {ORIENTATIONS.map((o) => (
            <button key={o.key} type="button"
                    className={`export-layout-fit${orientation === o.key ? ' active' : ''}`}
                    title={o.hint}
                    onClick={() => onOrientation(o.key)}>
              <span className="material-symbols-rounded">{o.icon}</span>
              <span className="export-layout-fit-label">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="export-layout-line">
        <span className="export-layout-key">Table sizing</span>
        <div className="export-layout-fits">
          {FITS.map((f) => (
            <button key={f.key} type="button"
                    className={`export-layout-fit${fit === f.key ? ' active' : ''}`}
                    title={f.hint}
                    onClick={() => onFit(f.key)}>
              <span className="material-symbols-rounded">{f.icon}</span>
              <span className="export-layout-fit-label">{f.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Only when it applies. A percentage box beside four other modes that
          ignore it is a control that lies about what it does. */}
      {fit === 'custom' && (
        <div className="export-layout-scale">
          <input
            type="range" min="25" max="150" step="5"
            value={Math.round((scale || 1) * 100)}
            onChange={(e) => onScale(Number(e.target.value) / 100)}
          />
          <span className="export-layout-scale-value">{Math.round((scale || 1) * 100)}%</span>
        </div>
      )}

      <p className="export-layout-note">{activeFit?.hint}</p>
    </div>
  );
}

export default ExportLayoutOptions;
